import httpClient from './http-client';
import {
  type AssetHistoryEvent,
  type AssetHistoryResponse,
  type ProcessedEvent,
  type TimelineEventType,
  type LovTimelineResponse,
  EventVerbiageMap,
  TimelineEventTypeEnum,
  getEventConfig,
} from "@/types/deviceHistoryTypes";
import i18n from "../i18n";

export class TimelineService {
  private baseApiEndpoint = '/api/dvcs';

  constructor(idToken?: string | undefined) {
    this.baseApiEndpoint = import.meta.env.VITE_BYPASS_PROXY === 'true'
                            ? '' : this.baseApiEndpoint;
  }

  // Static caches - only for essential APIs
  private static eventsCache: TimelineEventType[] | null = null;
  private static documentsAndDrawingsCache: Map<string, any[]> = new Map();

  // In-flight Promise maps — prevent duplicate concurrent HTTP calls caused by
  // React StrictMode double-mount or parallel effect invocations.
  private static assetHistoryInFlight: Map<string, Promise<ProcessedEvent[]>> = new Map();
  private static snDocsInFlight: Map<string, Promise<ProcessedEvent[]>> = new Map();

  // Validate API response helper method
  private validateApiResponse(response: any, operation: string): void {
    if (response.data && response.data.status === "failure") {
      throw new Error(response.data.message || `${operation} failed`);
    }

    if (response.status && response.status >= 400) {
      throw new Error(`${operation} failed with status: ${response.status}`);
    }
  }

  // Clear cache method (optional, for refreshing data if needed)
  clearCache(): void {
    TimelineService.eventsCache = null;
    TimelineService.documentsAndDrawingsCache.clear();
    TimelineService.assetHistoryInFlight.clear();
    TimelineService.snDocsInFlight.clear();
  }

  // ===== EVENT TYPES METHODS (from LovTimelineService) =====

  async getEventTypes(): Promise<TimelineEventType[]> {
    // Return cached data if available
    if (TimelineService.eventsCache !== null) {
      return TimelineService.eventsCache;
    }

    try {
      const response = await httpClient.get<LovTimelineResponse>(
        `${this.baseApiEndpoint}/devices/timeline/events`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.data?.data) {
        TimelineService.eventsCache = [];
        return [];
      }
      // Filter only the event types that should show in timeline
      const processedEvents = response.data.data
        .filter((event: TimelineEventType) => event.isVisible)
        .map((event: TimelineEventType) => ({
          ...event,
          event_name: EventVerbiageMap[event.event_name] || event.event_name, // default to API name
        }));

      // Cache the processed events
      TimelineService.eventsCache = processedEvents;
      return processedEvents;
    } catch (error) {
      console.error('Error fetching event types:', error);
      throw new Error(i18n.t('dhsFailedToFetchEventTypes', 'Failed to fetch event types'));
    }
  }

  // ===== FILE UPLOAD/DOWNLOAD METHODS =====

  // Generate SAS URL for file upload
  async generateUploadUrl(
    fileName: string
  ): Promise<{ uploadUrl: string; blobName: string }> {
    try {
      const response = await httpClient.get(
        `${this.baseApiEndpoint}/devices/generate-upload-url?file_name=${encodeURIComponent(fileName)}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      const data = response.data;
      const uploadData = data.upload_url || data;
      const uploadUrl = uploadData.upload_url;
      const blobName = uploadData.blob_name;
      if (!uploadUrl) {
        throw new Error(
          `No upload URL found in response. Available properties: ${Object.keys(
            uploadData
          ).join(", ")}`
        );
      }
      return { uploadUrl, blobName };
    } catch (error) {
      console.error('Error generating upload URL:', error);
      throw new Error(i18n.t('dhsFailedToGenerateUploadUrl', 'Failed to generate upload URL'));
    }
  }

  // Generate download URL for file using container_name and blob_name
  async generateDownloadUrl(
    containerName: string,
    originalName: string,
    blobName: string
  ): Promise<{ downloadUrl: string }> {
    try {
      const response = await httpClient.get(
        `${this.baseApiEndpoint}/devices/generate-download-url?container_name=${encodeURIComponent(
          containerName
        )}&blob_name=${encodeURIComponent(
          blobName
        )}&original_name=${encodeURIComponent(originalName)}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      const data = response.data;

      // Handle nested response structure: { "download_url": { "download_url": "actual_url", ... } }
      const downloadUrlData = data.download_url || data;
      const actualDownloadUrl =
        downloadUrlData.download_url || downloadUrlData.url || downloadUrlData;

      if (!actualDownloadUrl || typeof actualDownloadUrl !== "string") {
        throw new Error(
          `No valid download URL found in API response. Response structure: ${JSON.stringify(
            data,
            null,
            2
          )}`
        );
      }

      return { downloadUrl: actualDownloadUrl };
    } catch (error) {
      console.error('Error generating download URL:', error);
      throw new Error(i18n.t('dhsFailedToGenerateDownloadUrl', 'Failed to generate download URL'));
    }
  }

  // Upload file to SAS URL
  async uploadFile(uploadUrl: string, file: File): Promise<void> {
    try {
      const response = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
        headers: {
          "x-ms-blob-type": "BlockBlob",
        },
      });

      if (!response.ok) {
        throw new Error(`Upload failed with status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      throw new Error(i18n.t('dhsFailedToUploadFile', 'Failed to upload file'));
    }
  }

  // ===== EVENT CRUD METHODS =====

  // Create event after file upload
  async createEvent(
    deviceId: string,
    eventDate: string,
    eventTypeId: number,
    fileName: string,
    blobName: string,
    username: string
  ): Promise<void> {
    try {
      const body = {
        event_date: eventDate,
        event_type_id: eventTypeId,
        file_name: fileName,
        blob_name: blobName,
      };
      const response = await httpClient.post(
        `${this.baseApiEndpoint}/devices/${deviceId}/timeline/create-event?username=${encodeURIComponent(
          username
        )}`,
        body,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      this.validateApiResponse(response, "Create event");
    } catch (error) {
      console.error('Error creating event:', error);
      throw new Error(i18n.t('dhsFailedToCreateEvent', 'Failed to create event'));
    }
  }

  // Edit event by asset_history_id - handles all scenarios
  async editEvent(
    assetHistoryId: number,
    username: string,
    eventTypeId?: number,
    eventDate?: string,
    fileName?: string,
    blobName?: string,
    removeFile?: boolean
  ): Promise<void> {
    try {
      const body: any = {};
      if (eventTypeId !== undefined) {
        body.event_type_id = eventTypeId;
      }
      if (eventDate) {
        body.event_date = eventDate;
      }
      if (fileName && blobName) {
        body.file_name = fileName;
        body.blob_name = blobName;
      }
      if (removeFile) {
        body.remove_file = true;
      }
      const response = await httpClient.put(
        `${this.baseApiEndpoint}/devices/timeline/edit-event/${assetHistoryId}?username=${encodeURIComponent(
          username
        )}`,
        body,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      this.validateApiResponse(response, "Edit event");
    } catch (error) {
      console.error('Error editing event:', error);
      throw new Error(i18n.t('dhsFailedToEditEvent', 'Failed to edit event'));
    }
  }

  // Combined method for edit event with file upload (for scenarios 2, 3, 7)
  async editEventWithFile(
    assetHistoryId: number,
    file: File,
    username: string,
    eventTypeId?: number,
    eventDate?: string
  ): Promise<void> {
    try {
      // Step 1: Generate upload URL
      const { uploadUrl, blobName } = await this.generateUploadUrl(file.name);

      // Step 2: Upload file to Azure
      await this.uploadFile(uploadUrl, file);

      // Step 3: Update event with file info
      await this.editEvent(
        assetHistoryId,
        username,
        eventTypeId,
        eventDate,
        file.name,
        blobName
      );
    } catch (error) {
      console.error('Error editing event with file:', error);
      throw new Error(i18n.t('dhsFailedToEditEventWithFile', 'Failed to edit event with file'));
    }
  }

  // Delete event by asset_history_id
  async deleteEvent(
    assetHistoryId: number,
    username: string
  ): Promise<void> {
    try {
      const response = await httpClient.delete(
        `${this.baseApiEndpoint}/devices/timeline/delete-event/${assetHistoryId}?username=${encodeURIComponent(
          username
        )}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      this.validateApiResponse(response, "Delete event");
    } catch (error: any) {
      console.error('Error deleting event:', error);
      throw new Error(
        i18n.t('dhsFailedToDeleteEvent', 'Failed to delete event: {{error}}', {
          error: error?.response?.data?.message || error?.message || i18n.t('dhsUnknownError', 'Unknown error')
        })
      );
    }
  }

  // Combined method for the complete upload and create event flow
  async uploadFileAndCreateEvent(
    deviceId: string,
    file: File,
    eventDate: string,
    eventTypeId: number,
    username: string
  ): Promise<void> {
    try {
      // Step 1: Generate upload URL
      const { uploadUrl, blobName } = await this.generateUploadUrl(file.name);

      // Step 2: Upload file to SAS URL
      await this.uploadFile(uploadUrl, file);

      // Step 3: Create event
      await this.createEvent(
        deviceId,
        eventDate,
        eventTypeId,
        file.name,
        blobName,
        username
      );
    } catch (error) {
      console.error('Error uploading file and creating event:', error);
      throw new Error(i18n.t('dhsFailedToUploadAndCreateEvent', 'Failed to upload file and create event'));
    }
  }

  // ===== DATA FETCHING METHODS =====

  // Deduplication wrapper — if a request for this deviceId is already in flight,
  // return the same Promise instead of firing a second HTTP call.
  async getAssetHistory(deviceId: string): Promise<ProcessedEvent[]> {
    const existing = TimelineService.assetHistoryInFlight.get(deviceId);
    if (existing) return existing;

    const promise = this._fetchAssetHistory(deviceId);
    TimelineService.assetHistoryInFlight.set(deviceId, promise);
    promise.finally(() => TimelineService.assetHistoryInFlight.delete(deviceId));
    return promise;
  }

  private async _fetchAssetHistory(deviceId: string): Promise<ProcessedEvent[]> {
    const processedEvents: ProcessedEvent[] = [];

    // Add requestId to bypass disk cache and force DB call
    const requestId = new Date().getTime().toString();

    // Fetch asset history and SNDocs in parallel
    const [assetHistoryResult, snDocsResult] = await Promise.allSettled([
      httpClient.get<AssetHistoryResponse>(
        `${this.baseApiEndpoint}/devices/${deviceId}/timeline/asset-history?requestId=${requestId}`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      ),
      this.getSNDocs(deviceId),
    ]);

    // Process asset history result
    if (assetHistoryResult.status === 'fulfilled') {
      const data = assetHistoryResult.value.data;
      if (data?.results?.length) {
        const historyEvents = data.results[0]?.history_events || [];
        const serialNumber = data.results[0]?.serial_number || "";
        const baseModel = data.results[0]?.base_model || "";
        historyEvents.forEach((event: AssetHistoryEvent) => {
          const result = this.processEvent(event, serialNumber, baseModel);
          if (Array.isArray(result)) {
            if (result.length > 0) {
              processedEvents.push(...result);
            }
          } else if (result) {
            processedEvents.push(result);
          }
        });
      }
    } else {
      console.error('Error fetching asset history:', assetHistoryResult.reason);
    }

    // Process SNDocs result
    let snDocs: ProcessedEvent[] = [];
    if (snDocsResult.status === 'fulfilled') {
      snDocs = snDocsResult.value;
    } else {
      console.error('Error fetching SNDocs:', snDocsResult.reason);
    }

    // Combine both results
    const combinedEvents = [...processedEvents, ...snDocs];

    // If both fail, throw error
    if (combinedEvents.length === 0 && assetHistoryResult.status === 'rejected' && snDocsResult.status === 'rejected') {
      throw new Error(i18n.t('dhsFailedToFetchData', 'Failed to fetch asset history and SNDocs'));
    }

    return combinedEvents;
  }

  // Fetch SNdocs (Serial Specific Documents) with 3-tier guard:
  //   1. Result cache hit  → return immediately (no HTTP)
  //   2. In-flight Promise → return the same Promise (no duplicate HTTP)
  //   3. New fetch         → store Promise in Map, delete on settle
  // Errors are intentionally NOT caught here — they propagate to Promise.allSettled
  // in _fetchAssetHistory, which ensures asset history events still render if this fails.
  async getSNDocs(deviceId: string): Promise<ProcessedEvent[]> {
    // Tier 1 — result cache
    const cacheKey = `${deviceId}_serial_specific`;
    if (TimelineService.documentsAndDrawingsCache.has(cacheKey)) {
      const cachedData = TimelineService.documentsAndDrawingsCache.get(cacheKey)!;
      return this.processDocumentsToSNEvents(cachedData);
    }

    // Tier 2 — in-flight deduplication
    const existingInflight = TimelineService.snDocsInFlight.get(deviceId);
    if (existingInflight) return existingInflight;

    // Tier 3 — new fetch
    const promise = this._fetchSNDocs(deviceId);
    TimelineService.snDocsInFlight.set(deviceId, promise);
    promise.finally(() => TimelineService.snDocsInFlight.delete(deviceId));
    return promise;
  }

  private async _fetchSNDocs(deviceId: string): Promise<ProcessedEvent[]> {
    const cacheKey = `${deviceId}_serial_specific`;

    const response = await httpClient.get(
      `${this.baseApiEndpoint}/devices/${deviceId}/documents-and-drawings?category=serial_specific`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    const data = response.data;

    // Cache the raw data
    if (Array.isArray(data)) {
      TimelineService.documentsAndDrawingsCache.set(cacheKey, data);
    } else {
      TimelineService.documentsAndDrawingsCache.set(cacheKey, []);
    }

    return this.processDocumentsToSNEvents(data);
  }

  // ===== PRIVATE HELPER METHODS =====

  private processDocumentsToSNEvents(data: any[]): ProcessedEvent[] {
    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    // Filter documents for Quality and Repair Reports
    const filteredDocs = data.filter(
      (doc: any) =>
        doc.category === "Serial Specific Documents" &&
        ((doc.document_type === "Quality" &&
          doc.sub_document_type === "Calibration Certificate") ||
          doc.document_type === "Repair_Reports")
    );

    // Group documents by type and date
    const groupedDocs: Record<string, any[]> = {};
    filteredDocs.forEach((doc: any) => {
      const type = doc.document_type === "Quality" ? "Quality" : "Repair (Auto-added)";
      const dateKey = doc.document_date
        ? new Date(doc.document_date).toISOString().split("T")[0]
        : "unknown";
      const key = `${type}_${dateKey}`;

      if (!groupedDocs[key]) {
        groupedDocs[key] = [];
      }
      groupedDocs[key].push({
        document_title: doc.document_title,
        stream_url: doc.stream_url,
        document_date: doc.document_date,
        document_format: doc.document_format,
        document_type: doc.document_type,
        sub_document_type: doc.sub_document_type,
        category: doc.category,
      });
    });

    // Convert grouped documents to ProcessedEvent format
    const snEvents: ProcessedEvent[] = [];
    Object.entries(groupedDocs).forEach(([key, docs], index) => {
      const [type, dateKey] = key.split("_");
      const eventDate = dateKey !== "unknown" ? dateKey : null;

      snEvents.push({
        id: `sndocs_${type.toLowerCase()}_${index}`,
        title: type,
        date: eventDate,
        type: TimelineEventTypeEnum.FactoryDocs,
        isManual: false,
        documents: docs,
        details: {
          documentType: type,
          totalDocuments: docs.length,
        },
      });
    });

    return snEvents;
  }

  private processEvent(
      event: AssetHistoryEvent,
      serialNumber: string,
      baseModel: string
    ): ProcessedEvent | ProcessedEvent[] {
    let eventDate: string | null = event.created_date; // Default for other event types

    // Extract file_id from additional_details array
    let fileId: number | null = null;
    let eventDateFromDetails: string | null = null;

    if (Array.isArray(event.additional_details)) {
      event.additional_details.forEach((detail: any) => {
        if (detail.file_id) fileId = detail.file_id;
        if (detail.event_date) eventDateFromDetails = detail.event_date;
      });
    }

    // Use event_date from additional_details if available, otherwise fall back to created_date
    if (eventDateFromDetails) {
      eventDate = eventDateFromDetails;
    }

    // Check if this is a manual event (from /events API).
    // The single source of truth is TIMELINE_EVENT_CONFIG in deviceHistoryTypes.ts.
    const isSystemEvent = getEventConfig(event.event_type_name)?.isSystem ?? false;
    const isManualEvent = !isSystemEvent;

    const baseEvent = {
      id: event.id.toString(),
      date: eventDate,
      type: this.mapEventType(event.event_type_name),
      isManual: isManualEvent,
      details: {
        notes: event.notes,
        createdBy: event.created_by,
        sourceSystem: event.source_system,
        fileId: fileId,
        fileName: event.file_metadata?.file_name,
        containerName: event.file_metadata?.container_name,
        blobName: event.file_metadata?.blob_name,
        uploadedBy: event.file_metadata?.uploaded_by,
        ...event.additional_details,
      },
    };

    // Special handling: If Shipping Details contains warranty_end_date, create a Warranty event as well
    if (event.event_type_name === "Shipping Details") {
      // Support both array and object for additional_details
      let detailsObj = event.additional_details;
      if (Array.isArray(detailsObj)) {
        detailsObj = detailsObj[0] || {};
      }
      const events: ProcessedEvent[] = [];
      // Always add Shipping card
      events.push({
        ...baseEvent,
        date: detailsObj.ship_date || baseEvent.date,
        title: "Shipping",
        details: {
          shipDate: detailsObj.ship_date,
          warrantyEndDate: detailsObj.warranty_end_date,
          supportEndDate: detailsObj.support_end_date,
          ...detailsObj,
        },
      });
      // Conditionally add Warranty card if warranty_end_date is present
      if (detailsObj.warranty_end_date) {
        events.push({
          ...baseEvent,
          date: detailsObj.warranty_end_date,
          title: "Standard Warranty Expiration",
          type: TimelineEventTypeEnum.Warranty,
          details: {
            warrantyEndDate: detailsObj.warranty_end_date,
            ...detailsObj,
          },
        });
      }
      return events;
    }

    // Special handling: If Device Created/Updated contains support_end_date, create only a Support End Date event
    if (
      event.event_type_name === "Device Created" ||
      event.event_type_name === "Device Updated"
    ) {
      let detailsObj = Array.isArray(event.additional_details)
        ? event.additional_details[0] || {}
        : event.additional_details || {};

      if (detailsObj.support_end_date) {
        return {
          ...baseEvent,
          date: detailsObj.support_end_date,
          title: "Support End Date",
          type: TimelineEventTypeEnum.Support,
          details: {
            supportEndDate: detailsObj.support_end_date,
            ...detailsObj,
          },
        };
      }
      // Do not return a Device Created/Updated event - always return empty array
      return [];
    }

    switch (event.event_type_name) {
      case "Service Requested": {
        let requestType = "Service Request";
        if (Array.isArray(event.additional_details) && event.additional_details.length > 0) {
          requestType = event.additional_details[0].request_type || "Service Request";
        }
        return {
          ...baseEvent,
          title: "Service Requested",
          details: {
            ...baseEvent.details,
            requestType,
            serialNumber: serialNumber,
            baseModel: baseModel,
          },
        };
      }

      case "Walkdown":
        // For Walkdown, if additional_details is an array, create an event for each detail
        if (Array.isArray(event.additional_details) && event.additional_details.length > 0) {
          return event.additional_details.map((walkdownData: any, idx: number) => {
            // Use linked_date for marker placement
            const walkdownDetails = {
              id: walkdownData.id,
              walkdownNumber: walkdownData.walkdown_number,
              linkedDate: walkdownData.linked_date,
              walkdownCreationDate: walkdownData.walkdown_creation_date,
              recommendation: walkdownData.recommendation,
              visualHealthRating: walkdownData.visual_health_rating,
              comments: walkdownData.comments,

            };
            return {
              ...baseEvent,
              id: `${baseEvent.id}_${idx}`,
              date: walkdownData.linked_date || walkdownData.walkdown_creation_date || baseEvent.date,
              title: "Walkdown",
              details: {
                ...baseEvent.details,
                ...walkdownDetails,
              },
            };
          });
        }
        // Fallback: if no additional_details, return base event
        return {
          ...baseEvent,
          title: "Walkdown",
        };

      case "Device Added":
        return {
          ...baseEvent,
          title: TimelineEventTypeEnum.DeviceAdded,
          details: {
            ...baseEvent.details,
            turnaroundId : event.additional_details?.turnaround_id,
            turnaroundName: event.additional_details?.turnaround_name,
            turnaroundStartDate: event.additional_details?.turnaround_start_date,
            turnaroundEndDate: event.additional_details?.turnaround_end_date,
            tagNo: event.additional_details?.tag_no,
            isCritical: event.additional_details?.is_critical,
            addedBy: event.created_by,
          },
        };

      case "Device Removed":
        return {
          ...baseEvent,
          title: TimelineEventTypeEnum.DeviceRemoved,
          details: {
            ...baseEvent.details,
            turnaroundId : event.additional_details?.turnaround_id,
            turnaroundName: event.additional_details?.turnaround_name,
            turnaroundStartDate: event.additional_details?.turnaround_start_date,
            turnaroundEndDate: event.additional_details?.turnaround_end_date,
            tagNo: event.additional_details?.tag_no,
            isCritical: event.additional_details?.is_critical,
            removedBy: event.created_by,
          },
        };

      default:
        // For manual events, add "(Manually-added)" suffix to Repair events
        let title = this.mapEventType(event.event_type_name);
        if (isManualEvent && (title === "Repair" || event.event_type_name === "Repair Booked")) {
          title = "Repair (Manually-added)";
        }
        return {
          ...baseEvent,
          title: title,
        };
    }
  }

  private mapEventType(eventTypeName: string): string {
    // Single lookup — TIMELINE_EVENT_CONFIG is the one place to update display names.
    return getEventConfig(eventTypeName)?.displayName || eventTypeName;
  }
}